// Pure cycle-state logic — no store, no React, no dates from the ambient clock.
// The write gate is the whole point of this module, so it's kept small, total,
// and unit-tested rather than spread across components.
import type { Cycle, CycleRelation, CycleState } from '@/modules/cycles/types';

/**
 * Sort key for a cycle. Annual (`quarter: null`) sorts *before* Q1 of the same
 * year, matching the ordering the app has always used when comparing a
 * displayed cycle to the active one.
 */
export function cycleOrder(year: number, quarter?: number | null): number {
  return year * 10 + (quarter ?? 0);
}

/** Stable identity for a cycle. `"2026-3"`, or `"2026-annual"` for a year. */
export function cycleKey(year: number, quarter?: number | null): string {
  return quarter == null ? `${year}-annual` : `${year}-${quarter}`;
}

export function findCycle(
  cycles: Cycle[],
  year: number | null | undefined,
  quarter?: number | null,
): Cycle | null {
  if (year == null) return null;
  const key = cycleKey(year, quarter);
  return cycles.find((c) => cycleKey(c.year, c.quarter) === key) ?? null;
}

export interface WritableArgs {
  cycles: Cycle[];
  year: number | null | undefined;
  quarter?: number | null;
  canManage: boolean;
  /** The one cycle a manager has unlocked for this session, if any. */
  unlockedKey: string | null;
}

/**
 * Can anything be written to this cycle right now?
 *
 * - `open` — yes, subject to the caller's normal role permissions.
 * - `planning` — managers only. A quarter still being drafted isn't somewhere
 *   contributors should be filing work.
 * - `closed` — only a manager who has explicitly unlocked *this* cycle.
 *
 * Two deliberate escape hatches:
 *
 * `year == null` is always writable. Acyclic workspaces (`cyclicalPlanning:
 * false`) carry null year/quarter on everything, as do legacy "unknown cycle"
 * rows, and neither should be locked.
 *
 * A cycle with **no row** is writable. Locking must require an explicit
 * `closed` row, never a missing one — the app worked without this table for its
 * whole life, and a gap in backfill or a quarter created by an older code path
 * must not silently make people read-only.
 */
export function isCycleWritable({ cycles, year, quarter, canManage, unlockedKey }: WritableArgs): boolean {
  if (year == null) return true;
  const cycle = findCycle(cycles, year, quarter);
  if (!cycle) return true;
  switch (cycle.state) {
    case 'open': return true;
    case 'planning': return canManage;
    case 'closed': return canManage && unlockedKey === cycleKey(year, quarter);
  }
}

/**
 * Whether a quarter's plan is still a draft — nothing in it has been agreed to
 * yet, so a baseline taken now would freeze dates nobody has committed to
 * (#251).
 *
 * A quarter with no cycle row counts as a draft: it has never been activated,
 * which is how `isCycleWritable` and `cycle_is_closed` already read a missing
 * row. A cycle that has been closed is not a draft — its plan was agreed, and
 * closing it does not un-agree it.
 */
export function cycleIsDraft(
  cycles: Cycle[],
  year: number | null | undefined,
  quarter: number | null | undefined,
): boolean {
  if (year == null) return true;
  const cycle = findCycle(cycles, year, quarter);
  return !cycle || cycle.state === 'planning';
}

/** Where `(year, quarter)` sits relative to the primary cycle. */
export function cycleRelation(
  year: number | null | undefined,
  quarter: number | null | undefined,
  primaryYear: number | null | undefined,
  primaryQuarter: number | null | undefined,
): CycleRelation | null {
  if (year == null || primaryYear == null) return null;
  const a = cycleOrder(year, quarter);
  const b = cycleOrder(primaryYear, primaryQuarter);
  if (a < b) return 'past';
  if (a > b) return 'future';
  return 'current';
}

export function isPrimaryCycle(
  year: number | null | undefined,
  quarter: number | null | undefined,
  primaryYear: number | null | undefined,
  primaryQuarter: number | null | undefined,
): boolean {
  if (year == null || primaryYear == null) return false;
  return cycleOrder(year, quarter) === cycleOrder(primaryYear, primaryQuarter);
}

/**
 * The label shown wherever a cycle is named. Annual cycles read as the bare
 * year, matching how goals and the annual org chart already refer to themselves.
 */
export function cycleLabel(year: number | null | undefined, quarter?: number | null): string {
  if (year == null) return '—';
  return quarter == null ? String(year) : `Q${quarter} ${year}`;
}

/**
 * Merge the derived quarter list with the rows in `cycles`, so a quarter that
 * has been planned but has no data in it yet still appears. Sorted oldest-first,
 * matching what `availableQuarters` has always returned.
 */
export function mergeAvailableQuarters(
  derived: { year: number; quarter: number }[],
  cycles: Cycle[],
): { year: number; quarter: number }[] {
  const seen = new Map<string, { year: number; quarter: number }>();
  for (const q of derived) seen.set(cycleKey(q.year, q.quarter), q);
  for (const c of cycles) {
    if (c.quarter == null) continue; // annual cycles aren't quarters
    seen.set(cycleKey(c.year, c.quarter), { year: c.year, quarter: c.quarter });
  }
  return [...seen.values()].sort((a, b) => a.year - b.year || a.quarter - b.quarter);
}

/**
 * Open cycles other than the given one — what activation offers to close.
 * Running several quarters at once is allowed but exceptional, so the count
 * this returns is what the Activate CTA names.
 */
export function openCyclesExcept(
  cycles: Cycle[],
  year: number | null | undefined,
  quarter?: number | null,
): Cycle[] {
  const key = year == null ? null : cycleKey(year, quarter);
  return cycles.filter((c) => c.state === 'open' && cycleKey(c.year, c.quarter) !== key);
}

/** The earliest cycle still in `planning` — what Planning opens on by default. */
export function nearestPlanningCycle(cycles: Cycle[]): { year: number; quarter: number } | null {
  const inPlanning = cycles
    .filter((c) => c.state === 'planning' && c.quarter != null)
    .sort((a, b) => cycleOrder(a.year, a.quarter) - cycleOrder(b.year, b.quarter))[0];
  return inPlanning ? { year: inPlanning.year, quarter: inPlanning.quarter as number } : null;
}

export function nextQuarterOf(year: number, quarter: number): { year: number; quarter: number } {
  return quarter >= 4 ? { year: year + 1, quarter: 1 } : { year, quarter: quarter + 1 };
}

/**
 * The calendar quarter a date falls in.
 *
 * Deliberately calendar-only: it knows nothing about which cycles exist or what
 * state they're in, so it can serve as the *default* for a quarter the user is
 * still free to change. Callers that need the answer constrained to real cycles
 * should intersect the result with `availableQuarters` themselves rather than
 * having that policy baked in here.
 *
 * Accepts the `YYYY-MM-DD` strings the app stores dates as, parsed as local time
 * — `new Date('2026-01-01')` alone is UTC midnight, which lands in the previous
 * quarter for anyone west of Greenwich.
 */
export function calendarQuarterOf(date: string | Date): { year: number; quarter: number } {
  const d = typeof date === 'string' ? new Date(`${date.slice(0, 10)}T00:00:00`) : date;
  return { year: d.getFullYear(), quarter: Math.floor(d.getMonth() / 3) + 1 };
}

/**
 * Which quarters can have planning *started* on them.
 *
 * Three candidates — the one after the furthest quarter on record, the current
 * one, and the one after that — minus any that already exist, minus any that
 * have already ended.
 *
 * The three-candidate shape is what lets a team that skipped Q2 and Q3 plan Q4
 * when they come back in Q4, without also letting someone in Q1 plan Q4 before
 * the quarters in between exist. The already-ended filter is what stops "the
 * one after the furthest on record" from offering a quarter in the past in
 * exactly that catch-up case.
 */
export function plannableQuarters(
  existing: { year: number; quarter: number }[],
  today: { year: number; quarter: number },
): { year: number; quarter: number }[] {
  const furthest = [...existing].sort((a, b) => cycleOrder(b.year, b.quarter) - cycleOrder(a.year, a.quarter))[0];
  const candidates = [
    furthest ? nextQuarterOf(furthest.year, furthest.quarter) : null,
    { year: today.year, quarter: today.quarter },
    nextQuarterOf(today.year, today.quarter),
  ].filter((c): c is { year: number; quarter: number } => c != null);

  const taken = new Set(existing.map((q) => cycleKey(q.year, q.quarter)));
  const todayOrder = cycleOrder(today.year, today.quarter);
  const seen = new Set<string>();

  return candidates
    .filter((c) => {
      const key = cycleKey(c.year, c.quarter);
      if (taken.has(key) || seen.has(key)) return false;
      // Never plan into the past — a quarter that has already ended can't be planned.
      if (cycleOrder(c.year, c.quarter) < todayOrder) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => cycleOrder(a.year, a.quarter) - cycleOrder(b.year, b.quarter));
}

/**
 * Cycles that have drifted out of step with the calendar, and how far.
 *
 * Two kinds, both a leader's call to make rather than something to automate:
 * a cycle still `open` after its quarter ended, and a `planning` cycle whose
 * quarter has already started. These are the nudge that makes multiple open
 * quarters *discouraged* rather than forbidden — a rule would have blocked the
 * legitimate boundary case along with the sloppy one.
 *
 * Reported at day 0 / 7 / 14 past the line and no further: three prompts is a
 * reminder, a permanent one is furniture (§7.4).
 */
export type CycleDriftKind = 'open-past-end' | 'planning-past-start';

export interface CycleDrift {
  cycle: Cycle;
  kind: CycleDriftKind;
  /** Which of the three reminders this is — 0, 7 or 14 days past. */
  interval: 0 | 7 | 14;
  daysPast: number;
}

export function cycleDrift(
  cycles: Cycle[],
  todayStr: string,
  bounds: (year: number, quarter: number) => { start: string; end: string },
  daysBetween: (fromStr: string, toStr: string) => number,
): CycleDrift[] {
  const out: CycleDrift[] = [];
  for (const c of cycles) {
    if (c.quarter == null) continue; // annual drift has no surface yet
    const { start, end } = bounds(c.year, c.quarter);
    let kind: CycleDriftKind | null = null;
    let since: string | null = null;
    if (c.state === 'open' && todayStr > end) { kind = 'open-past-end'; since = end; }
    else if (c.state === 'planning' && todayStr >= start) { kind = 'planning-past-start'; since = start; }
    if (!kind || !since) continue;

    const daysPast = daysBetween(since, todayStr);
    // Only fire on the three marks, so a dismissal actually buys a week of quiet.
    const interval = daysPast >= 14 ? 14 : daysPast >= 7 ? 7 : 0;
    if (daysPast > 14 + 7) continue; // past the last reminder, stop nagging
    out.push({ cycle: c, kind, interval, daysPast });
  }
  return out.sort((a, b) => b.daysPast - a.daysPast);
}

/** The state a cycle should get when backfilled or created relative to the primary. */
export function stateRelativeToPrimary(
  year: number,
  quarter: number | null,
  primaryYear: number | null,
  primaryQuarter: number | null,
): CycleState {
  const rel = cycleRelation(year, quarter, primaryYear, primaryQuarter);
  if (rel === 'current') return 'open';
  if (rel === 'future') return 'planning';
  return 'closed';
}

export interface DisplayedCycleArgs {
  workspaceId: string;
  previousWorkspaceId: string | null;
  /** True only when this load is a team change inside a workspace already open. */
  teamChanged: boolean;
  displayed: { year: number | null; quarter: number | null };
  active: { year: number | null; quarter: number | null };
  availableQuarters: { year: number; quarter: number }[];
}

export function resolveDisplayedCycle({
  workspaceId, previousWorkspaceId, teamChanged, displayed, active, availableQuarters,
}: DisplayedCycleArgs): { year: number | null; quarter: number | null; carried: boolean } {
  const carried = teamChanged
    && previousWorkspaceId === workspaceId
    && displayed.year != null
    && (displayed.quarter == null
      || availableQuarters.some((c) => c.year === displayed.year && c.quarter === displayed.quarter));

  return carried
    ? { year: displayed.year, quarter: displayed.quarter, carried: true }
    : { year: active.year, quarter: active.quarter, carried: false };
}
